"use client"

import React from 'react'
import { useCurrentSearchParams } from '@/shared/hooks/use-current-search-params'
import {
    GetWorkOrderQueryParamsType,
    WORK_ORDERS_LIST_DEFAULT_LIMIT,
} from '../schemas/query-params.schema'
import { Badge } from '@/shared/components/ui/badge'
import { X } from 'lucide-react'
import { useRouter, usePathname } from 'next/navigation'
import { buildUrl } from '@/shared/lib/utils'

function WorkOrdersFilterIndicator() {
    const router = useRouter()
    const pathname = usePathname()
    
    const currentParams = useCurrentSearchParams<GetWorkOrderQueryParamsType>([
        "status",
        "region",
        "teamName",
        "delegateTo",
        "workOrderType",
        "progressMin",
        "progressMax",
        "homeownerName",
        "city",
        "street",
        "ownerId",
        "createdDate",
    ])

    const activeFilters = Object.entries(currentParams).filter(([key, value]) => 
        value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0)
    )

    if (activeFilters.length === 0) {
        return null
    }

    const removeFilter = (key: string) => {
        const newParams = { ...currentParams }
        delete newParams[key as keyof GetWorkOrderQueryParamsType]
        
        const url = buildUrl(
            pathname,
            newParams,
            {
                previousParams: {
                    page: 1,
                    limit: WORK_ORDERS_LIST_DEFAULT_LIMIT,
                }
            }
        )
        router.push(url)
    }

    const clearAllFilters = () => {
        const url = buildUrl(
            pathname,
            {},
            {
                previousParams: {
                    page: 1,
                    limit: WORK_ORDERS_LIST_DEFAULT_LIMIT,
                }
            }
        )
        router.push(url)
    }

    const formatFilterLabel = (key: string): string => {
        const labels: Record<string, string> = {
            status: 'Status',
            region: 'Region',
            teamName: 'Team Name',
            delegateTo: 'Team Lead',
            workOrderType: 'Work Order',
            progressMin: 'Progress Min',
            progressMax: 'Progress Max',
            homeownerName: 'Homeowner Name',
            city: 'Homeowner City',
            street: 'Homeowner Street',
            ownerId: 'Workorder Owner',
            createdDate: 'Created Date',
        }
        return labels[key] || key
    }

    const formatFilterValue = (key: string, value: any): string => {
        if (key === 'status') {
            if (value === 'Active') return 'Open'
            if (value === 'Inactive') return 'Closed'
        }
        if (key === 'progressMin' || key === 'progressMax') {
            return `${value}%`
        }
        if (Array.isArray(value)) {
            return value.join(', ')
        }
        return String(value)
    }

    return (
        <div className="flex flex-wrap items-center gap-2 py-2">
            <span className="text-sm text-muted-foreground">Active Filters:</span>
            {activeFilters.map(([key, value]) => (
                <Badge 
                    key={key} 
                    variant="secondary" 
                    className="flex items-center gap-1 px-3 py-1"
                >
                    <span className="font-medium">{formatFilterLabel(key)}:</span>
                    <span>{formatFilterValue(key, value)}</span>
                    <button
                        onClick={() => removeFilter(key)}
                        className="ml-1 hover:bg-muted rounded-full p-0.5"
                        aria-label={`Remove ${formatFilterLabel(key)} filter`}
                    >
                        <X className="h-3 w-3" />
                    </button>
                </Badge>
            ))}
            <button
                onClick={clearAllFilters}
                className="text-sm text-primary hover:underline"
            >
                Clear all
            </button>
        </div>
    )
}

export default WorkOrdersFilterIndicator

